import React from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  VStack, Text, Button, ButtonSpinner, ButtonText, HStack,
} from '@gluestack-ui/themed';
import dayjs from 'dayjs';
import FormField from '../../../common/components/forms/FormField';
import TaskBindingList from './TaskBindingList';
import { useEventCalendarData } from '../hooks/useEventCalendarData';
import { useEventCalendarTimeLogic } from '../hooks/useEventCalendarTimeLogic';
import { useEventCalendarMutations } from '../hooks/useEventCalendarMutations';
import { useTenantEventKinds } from '../../../common/context/TenantSettingsStore';
import {
  DEFAULT_SLOT_VALUES,
  FORM_FIELD_CONFIG,
  VALIDATION_RULES,
  validateFutureTime,
} from '../constants/slotConstants';
import { formatSlotDate, getTimezoneDisplayInfo, timezoneHasDST } from '../utils/slotUtils';
import { useUserFeatures } from '../../../common/hooks/useUserFeatures';
import { filterVisibleEventKinds } from '../../../common/utils/eventKindVisibility';

/**
 * SlotCreateForm Component - Handles creation of appointment slots
 */
export const getSelectedTasksForEventKind = ({
  allTasks = [],
  eventKindId,
  selectedTaskIds = {},
}) => {
  if (!eventKindId) return [];

  const activeTaskIds = new Set(
    Object.keys(selectedTaskIds).filter((taskId) => selectedTaskIds?.[taskId]),
  );
  if (!activeTaskIds.size) return [];

  const selectedTasksById = new Map();
  allTasks.forEach((task) => {
    if (!activeTaskIds.has(task.id)) return;
    if (task?.eventConfig?.eventKindId !== eventKindId) return;
    if (!selectedTasksById.has(task.id)) {
      selectedTasksById.set(task.id, task);
    }
  });

  return Array.from(selectedTasksById.values());
};

export const resolveSlotParameters = ({ selectedKind, selectedTasks = [] }) => {
  const defaultDuration = selectedKind?.durationMin || 30;
  const defaultCapacity = selectedKind?.maxCapacity || 1;

  if (!selectedKind || selectedTasks.length === 0) {
    return {
      durationMinutes: defaultDuration,
      capacity: defaultCapacity,
      hasConflict: false,
      source: 'eventKind',
    };
  }

  const resolvedSets = selectedTasks.map((task) => ({
    durationMinutes: task?.eventConfig?.durationMinutesOverride ?? defaultDuration,
    capacity: task?.eventConfig?.capacityOverride ?? defaultCapacity,
  }));
  const uniqueSets = new Set(resolvedSets.map((value) => `${value.durationMinutes}:${value.capacity}`));

  if (uniqueSets.size !== 1) {
    return {
      durationMinutes: defaultDuration,
      capacity: defaultCapacity,
      hasConflict: true,
      source: 'conflict',
    };
  }

  const hasTaskOverride = selectedTasks.some(
    (task) => task?.eventConfig?.durationMinutesOverride != null || task?.eventConfig?.capacityOverride != null,
  );

  return {
    ...resolvedSets[0],
    hasConflict: false,
    source: hasTaskOverride ? 'task' : 'eventKind',
  };
};

export const validateSlotCapacity = (value, kind, t) => {
  if (!kind?.allowCustomCapacity || value === '' || value === null || value === undefined) {
    return true;
  }
  const n = Number(value);
  if (Number.isNaN(n)) {
    return t('lecturer.moduleEditor.taskEditor.eventConfig.errors.mustBeNumber');
  }
  const min = kind.minCapacityOverride ?? 1;
  const max = kind.maxCapacityOverride ?? 1000;
  if (n < min) {
    return t('lecturer.moduleEditor.taskEditor.eventConfig.errors.capacityMin', { min });
  }
  if (n > max) {
    return t('lecturer.moduleEditor.taskEditor.eventConfig.errors.capacityMax', { max });
  }
  return true;
};

const SlotCreateForm = ({
  date, viewMode, onSuccess, existingSlots = [],
}) => {
  const { t } = useTranslation();
  const { allTasks } = useEventCalendarData();
  const { eventKinds, getEventKindById } = useTenantEventKinds();
  const { createSlot, createMutation } = useEventCalendarMutations(onSuccess);
  const { flags } = useUserFeatures();

  // Selected date comes from parent calendar click
  const selectedDate = React.useMemo(() => (date ? new Date(date) : new Date()), [date]);

  // Get timezone info for the TARGET date (important for DST-aware display)
  const { isDST, shortName: tzShort } = React.useMemo(
    () => getTimezoneDisplayInfo('en', selectedDate),
    [selectedDate],
  );

  // Check if user's timezone observes DST (to decide whether to show Summer/Winter Time)
  const hasDST = React.useMemo(() => timezoneHasDST(), []);

  // Get the DST label for the target date (only relevant if timezone has DST)
  const dstLabel = React.useMemo(() => {
    if (!hasDST) return '';
    return isDST
      ? t('lecturer.eventCalendar.summerTime')
      : t('lecturer.eventCalendar.winterTime');
  }, [hasDST, isDST, t]);

  // Init form first so we can use watch in time logic constraints
  const methods = useForm({
    defaultValues: {
      ...DEFAULT_SLOT_VALUES,
      // Initial placeholder; actual default is computed below and applied via effect
      startTime: DEFAULT_SLOT_VALUES.startTime,
    },
  });

  const {
    handleSubmit, control, setValue, getValues, resetField,
  } = methods;
  const eventKindId = useWatch({ control, name: 'eventKindId' });
  const selectedTaskIds = useWatch({ control, name: 'selectedTaskIds' }) || {};
  const duration = useWatch({ control, name: 'durationMinutes' });
  const selectedKind = getEventKindById(eventKindId);
  const selectedTasks = getSelectedTasksForEventKind({
    allTasks,
    eventKindId,
    selectedTaskIds,
  });
  const resolvedSlotParameters = resolveSlotParameters({ selectedKind, selectedTasks });
  const hasTaskParameterConflict = resolvedSlotParameters.hasConflict;
  const isCapacityEditable = !!selectedKind?.allowCustomCapacity && !selectedKind?.requiresTaskBinding;

  const capacityValidation = React.useMemo(() => ({
    validate: (value) => validateSlotCapacity(value, selectedKind, t),
  }), [selectedKind, t]);

  const { defaultTime } = useEventCalendarTimeLogic(selectedDate, existingSlots, viewMode, {
    allowedTimeStart: selectedKind?.allowedTimeStart,
    allowedTimeEnd: selectedKind?.allowedTimeEnd,
    durationMinutes: duration,
  });

  // Update effective slot parameters when event kind or bound tasks change
  React.useEffect(() => {
    const currentDuration = getValues('durationMinutes');

    if (currentDuration !== resolvedSlotParameters.durationMinutes) {
      setValue('durationMinutes', resolvedSlotParameters.durationMinutes, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (!isCapacityEditable) {
      // clearErrors mutates the same error object, so the field keeps the
      // previous kind's max error. resetField notifies subscribers.
      resetField('capacity', { defaultValue: resolvedSlotParameters.capacity });
    }
  }, [
    getValues,
    resetField,
    isCapacityEditable,
    resolvedSlotParameters.capacity,
    resolvedSlotParameters.durationMinutes,
    setValue,
  ]);

  // Seed editable capacity default when switching to a custom-capacity event kind
  React.useEffect(() => {
    if (isCapacityEditable) {
      setValue('capacity', resolvedSlotParameters.capacity, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [eventKindId]); // eslint-disable-line react-hooks/exhaustive-deps -- seed once per kind change only

  // Sync start time to computed default when it changes, without causing loops
  React.useEffect(() => {
    const current = getValues('startTime');
    if (current !== defaultTime) {
      setValue('startTime', defaultTime, { shouldDirty: false, shouldValidate: false });
    }
  }, [defaultTime, getValues, setValue]);

  const eventKindChoices = filterVisibleEventKinds(eventKinds, flags, eventKindId)
    .map((k) => ({ label: k.label, value: k.id }));

  // Validate that selected start time is within allowed window for the chosen event kind
  const validateWithinAllowedWindow = React.useCallback(
    (timeValue) => {
      if (!selectedKind || !timeValue) return true;
      const { allowedTimeStart, allowedTimeEnd } = selectedKind || {};
      if (!allowedTimeStart && !allowedTimeEnd) return true;

      const [hh, mm] = String(timeValue)
        .split(':')
        .map((n) => Number(n));
      if (Number.isNaN(hh) || Number.isNaN(mm)) return 'HH:mm';

      // Convert UTC HH:mm boundaries to local Dayjs for the selected date
      const utcHHMMToLocalDate = (hhmm) => {
        if (!hhmm) return null;
        const [h, m] = String(hhmm)
          .split(':')
          .map((n) => Number(n));
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        const y = dayjs(selectedDate).year();
        const mo = dayjs(selectedDate).month();
        const d = dayjs(selectedDate).date();
        const utcMillis = Date.UTC(y, mo, d, h, m, 0, 0);
        return dayjs(new Date(utcMillis));
      };

      const toLocalHHMM = (hhmm) => {
        const dt = utcHHMMToLocalDate(hhmm);
        return dt ? dt.format('HH:mm') : '';
      };

      const startDT = dayjs(selectedDate).hour(hh).minute(mm).second(0)
        .millisecond(0);
      const dur = Number(duration) || 0;
      const endDT = startDT.add(dur, 'minute');

      const minDT = utcHHMMToLocalDate(allowedTimeStart);
      const maxDT = utcHHMMToLocalDate(allowedTimeEnd);
      const windowStart = toLocalHHMM(allowedTimeStart);
      const windowEnd = toLocalHHMM(allowedTimeEnd);

      if (minDT && startDT.isBefore(minDT)) {
        const errorSuffix = dstLabel ? ` (${dstLabel})` : '';
        return (
          t('lecturer.eventCalendar.errors.mustStartAtOrAfter', { start: windowStart, dstLabel })
          || `Start must be at or after ${windowStart}${errorSuffix}`
        );
      }

      if (maxDT && endDT.valueOf() > maxDT.valueOf()) {
        const errorSuffix = dstLabel ? ` (${dstLabel})` : '';
        return (
          t('lecturer.eventCalendar.errors.mustEndByWindow', {
            start: windowStart,
            end: windowEnd,
            dstLabel,
          }) || `End must be by ${windowEnd}${errorSuffix}`
        );
      }

      return true;
    },
    [selectedKind, selectedDate, duration, t, dstLabel],
  );

  // Coercion effect removed; defaultTime already respects constraints

  const onSubmit = (formData) => {
    createSlot(formData, selectedDate, selectedTasks, selectedKind);
  };

  // Helper to render the allowed window info with DST-aware label
  const renderAllowedWindowInfo = () => {
    if (!selectedKind) return null;
    const { allowedTimeStart, allowedTimeEnd } = selectedKind;
    if (!allowedTimeStart && !allowedTimeEnd) return null;

    const utcHHMMToLocalDate = (hhmm) => {
      if (!hhmm) return null;
      const [h, m] = String(hhmm)
        .split(':')
        .map((n) => Number(n));
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      const y = dayjs(selectedDate).year();
      const mo = dayjs(selectedDate).month();
      const d = dayjs(selectedDate).date();
      const utcMillis = Date.UTC(y, mo, d, h, m, 0, 0);
      return dayjs(new Date(utcMillis));
    };

    const toLocalHHMM = (hhmm) => {
      const dt = utcHHMMToLocalDate(hhmm);
      return dt ? dt.format('HH:mm') : '';
    };

    const startStr = allowedTimeStart ? toLocalHHMM(allowedTimeStart) : '00:00';
    const endStr = allowedTimeEnd ? toLocalHHMM(allowedTimeEnd) : '23:59';

    // Build the valid times text: "Valid times: 14:00-18:00" or "Valid times: 14:00-18:00 (Summer Time)"
    const times = `${startStr}–${endStr}`;
    const windowText = dstLabel
      ? t('lecturer.eventCalendar.validTimesWithDst', {
        times,
        dstLabel,
        defaultValue: `Valid times: ${times} (${dstLabel})`,
      })
      : t('lecturer.eventCalendar.validTimesSimple', {
        times,
        defaultValue: `Valid times: ${times}`,
      });

    return (
      <Text size="xs" color="$textSubtle" mt="$1">
        {windowText}
      </Text>
    );
  };

  const renderParameterSourceInfo = () => {
    if (!selectedKind) return null;
    if (hasTaskParameterConflict) {
      return (
        <Text size="xs" color="$statusError" mt="$1">
          {t('lecturer.eventCalendar.sidebar.taskParameterConflict')}
        </Text>
      );
    }
    return (
      <Text size="xs" color="$textSubtle" mt="$1">
        {resolvedSlotParameters.source === 'task'
          ? t('lecturer.eventCalendar.sidebar.parametersFromTask')
          : t('lecturer.eventCalendar.sidebar.parametersFromEventKind')}
      </Text>
    );
  };

  return (
    <FormProvider {...methods}>
      <VStack space="md" flex={1}>
        {/* Target date - clean header without timezone clutter */}
        <Text size="md" fontWeight="semibold" color="$textHeading" mb="$1">
          {formatSlotDate(selectedDate)}
        </Text>

        {/* Event Kind */}
        <VStack space="xs">
          <FormField
            name="eventKindId"
            type="select"
            label="Event Kind"
            choices={eventKindChoices}
            horizontal
            fieldWidth={FORM_FIELD_CONFIG.eventKind.width}
            defaultValue=""
            rules={VALIDATION_RULES.eventKind}
            fieldSize={FORM_FIELD_CONFIG.eventKind.size}
          />
          {renderAllowedWindowInfo()}
        </VStack>

        {/* Time input - timezone shown in label */}
        <FormField
          name="startTime"
          label={`${t('lecturer.eventCalendar.sidebar.startTime')} (${tzShort})`}
          type="text"
          horizontal
          fieldWidth={FORM_FIELD_CONFIG.timeInput.width}
          defaultValue={defaultTime}
          rules={{
            ...VALIDATION_RULES.startTime,
            validate: (value) => {
              const future = validateFutureTime(value, selectedDate);
              if (future !== true) return future;
              return validateWithinAllowedWindow(value);
            },
          }}
          fieldSize={FORM_FIELD_CONFIG.timeInput.size}
        />

        {/* Duration */}
        <FormField
          name="durationMinutes"
          label={t('lecturer.eventCalendar.sidebar.duration')}
          type="text"
          horizontal
          readonly
          fieldWidth={FORM_FIELD_CONFIG.duration.width}
          defaultValue={30}
          fieldSize={FORM_FIELD_CONFIG.duration.size}
        />

        {/* Capacity */}
        <FormField
          name="capacity"
          label={t('lecturer.eventCalendar.sidebar.maxStudents')}
          type="text"
          horizontal
          readonly={!isCapacityEditable}
          fieldWidth={FORM_FIELD_CONFIG.capacity.width}
          defaultValue={1}
          fieldSize={FORM_FIELD_CONFIG.capacity.size}
          rules={capacityValidation}
        />
        {renderParameterSourceInfo()}

        {/* Task binding section */}
        {selectedKind?.requiresTaskBinding && (
          <VStack flex={1}>
            <TaskBindingList eventKindId={eventKindId} />
          </VStack>
        )}

        {/* Submit button */}
        <Button
          onPress={handleSubmit(onSubmit)}
          disabled={createMutation.isPending}
          size="sm"
        >
          {createMutation.isPending ? (
            <HStack space="sm" alignItems="center">
              <ButtonSpinner />
              <ButtonText>{t('lecturer.eventCalendar.sidebar.creatingSlot')}</ButtonText>
            </HStack>
          ) : (
            <ButtonText>{t('common.buttons.save')}</ButtonText>
          )}
        </Button>
      </VStack>
    </FormProvider>
  );
};

export default SlotCreateForm;
